<template>
    <div class="ir-heatmap-viewer">
        <div v-if="displaySrc" class="viewer-toolbar">
            <button class="tool-btn" type="button" @click.stop="resetTransform">{{ tr("复位") }}</button>
        </div>
        <div
            ref="stage"
            class="viewer-stage"
            @wheel.prevent="onWheel"
            @mousedown="onPointerDown"
            @mousemove="onPointerMove"
            @mouseup="onPointerUp"
            @mouseleave="onPointerUp"
            @touchstart.prevent="onTouchStart"
            @touchmove.prevent="onTouchMove"
            @touchend="onTouchEnd"
            @touchcancel="onTouchEnd"
        >
            <p v-if="!displaySrc" class="image-unavailable">{{ tr('暂无热图') }}</p>
            <div v-else class="viewer-content" :style="contentStyle">
                <!-- 按热图原始比例算出的精确显示盒：img 与 SVG 同宽高，1:1 对齐 -->
                <div v-if="fit.w && fit.h" class="media-fit" :style="fitStyle">
                    <img
                        class="heatmap-img"
                        :src="displaySrc"
                        :alt="tr('红外热图')"
                        draggable="false"
                        @load="onImageLoad"
                        @error="onImageError"
                    />
                    <svg
                        v-if="imgSize.w && imgSize.h && drawableRois.length"
                        class="roi-overlay"
                        :viewBox="`0 0 ${imgSize.w} ${imgSize.h}`"
                        preserveAspectRatio="none"
                    >
                        <rect
                            v-for="roi in drawableRois"
                            :key="roi.key + '-' + roi.view"
                            :x="roi.rect.x"
                            :y="roi.rect.y"
                            :width="roi.rect.width"
                            :height="roi.rect.height"
                            :rx="roi.rect.radius"
                            :ry="roi.rect.radius"
                            :class="{ 'is-active': roi.key === selectedKey }"
                            tabindex="0" role="button" :aria-label="roi.key"
                            @keydown.enter.stop="onSelectRoi(roi.key)"
                            @keydown.space.stop.prevent="onSelectRoi(roi.key)"
                            @click.stop="onSelectRoi(roi.key)"
                            @touchend.stop.prevent="onSelectRoi(roi.key)"
                        />
                    </svg>
                </div>
                <!-- 仅首次尚无尺寸时用探针拿 naturalWidth；切图不再拆掉主图，避免偶发空白 -->
                <img
                    v-else
                    class="heatmap-img is-probe"
                    :src="displaySrc"
                    alt=""
                    draggable="false"
                    @load="onImageLoad"
                    @error="onImageError"
                />
            </div>
        </div>
    </div>
</template>

<script>
import { domesticText } from '@/assets/js/m60sDomesticReport'
const FALLBACK_IMG = ''

export default {
    name: 'InfraredHeatmapViewer',
    props: {
        imageUrl: { type: String, required: true },
        preloadUrls: { type: Array, default: () => [] },
        view: { type: String, default: 'front' },
        rois: { type: Array, default: () => [] },
        selectedKey: { type: String, default: '' },
    },
    data() {
        return {
            imgSize: { w: 0, h: 0 },
            stageSize: { w: 0, h: 0 },
            transform: { scale: 1, x: 0, y: 0 },
            dragging: {
                active: false,
                moved: false,
                startX: 0,
                startY: 0,
                originX: 0,
                originY: 0,
            },
            pinch: {
                active: false,
                startDist: 0,
                startScale: 1,
            },
            displayView: 'front',
            displayRois: [],
            /** CDN 签名失效/404 时回退，避免微信里破图「?」icon */
            displaySrc: '',
            resizeObserver: null,
        }
    },
    computed: {
        contentStyle() {
            const { scale, x, y } = this.transform
            return {
                transform: `translate(${x}px, ${y}px) scale(${scale})`,
            }
        },
        /** 在 stage 内按原图比例 contain 后的精确像素盒 */
        fit() {
            const iw = this.imgSize.w
            const ih = this.imgSize.h
            const sw = this.stageSize.w
            const sh = this.stageSize.h
            if (!iw || !ih || !sw || !sh) return { w: 0, h: 0, left: 0, top: 0 }
            // 热图四周留白多，纯 contain 人体偏小；略放大裁掉边缘空白（stage overflow:hidden）
            const FILL = 1.32
            const scale = Math.min(sw / iw, sh / ih) * FILL
            const w = Math.max(1, Math.floor(iw * scale))
            const h = Math.max(1, Math.floor(ih * scale))
            return {
                w,
                h,
                left: Math.floor((sw - w) / 2),
                top: Math.floor((sh - h) / 2),
            }
        },
        fitStyle() {
            return {
                width: `${this.fit.w}px`,
                height: `${this.fit.h}px`,
                left: `${this.fit.left}px`,
                top: `${this.fit.top}px`,
            }
        },
        viewRois() {
            return (this.displayRois || []).filter(roi =>
                roi && roi.view === this.displayView && roi.key !== 'hip' && Array.isArray(roi.points)
            )
        },
        /** 当前视角全部 ROI，便于点击圈圈切换部位 */
        drawableRois() {
            return this.viewRois
                .map(roi => {
                    const rect = this.toBoundingRect(this.normalizePoints(roi.points))
                    if (!rect) return null
                    return { ...roi, rect }
                })
                .filter(Boolean)
        },
    },
    watch: {
        imageUrl: { immediate: true, handler() { this.prepareImage() } },
        view() { this.prepareImage() },
        rois() { this.prepareImage() },
        preloadUrls: {
            immediate: true,
            handler(urls) { urls.filter(Boolean).forEach(url => this.decodedImage(url).catch(() => {})) },
        },
    },
    mounted() {
        this.measureStage()
        if (typeof ResizeObserver !== 'undefined' && this.$refs.stage) {
            this.resizeObserver = new ResizeObserver(() => this.measureStage())
            this.resizeObserver.observe(this.$refs.stage)
        }
        window.addEventListener('resize', this.measureStage)
    },
    beforeDestroy() {
        this._imageRequest = (this._imageRequest || 0) + 1
        if (this._imageCache) this._imageCache.clear()
        if (this.resizeObserver) this.resizeObserver.disconnect()
        window.removeEventListener('resize', this.measureStage)
    },
    methods: {
        tr(text) { return domesticText(this.$i18n.locale, text) },
        decodedImage(url) {
            if (!this._imageCache) this._imageCache = new Map()
            if (this._imageCache.has(url)) return this._imageCache.get(url)
            const promise = new Promise((resolve, reject) => {
                const img = new Image()
                img.onload = async () => {
                    try {
                        if (img.decode) await img.decode()
                        if (!img.naturalWidth || !img.naturalHeight) throw new Error('Empty heatmap')
                        resolve(img)
                    } catch (error) { reject(error) }
                }
                img.onerror = reject
                img.src = url
            })
            this._imageCache.set(url, promise)
            // Only retain a few decoded views when changing history reports.
            if (this._imageCache.size > 4) this._imageCache.delete(this._imageCache.keys().next().value)
            promise.catch(() => {
                if (this._imageCache.get(url) === promise) this._imageCache.delete(url)
            })
            return promise
        },
        async prepareImage() {
            const request = this._imageRequest = (this._imageRequest || 0) + 1
            const url = this.imageUrl
            const view = this.view
            if (!url) {
                this.displaySrc = FALLBACK_IMG
                this.displayView = view
                return
            }
            try {
                const img = await this.decodedImage(url)
                if (request !== this._imageRequest) return
                // Commit pixels, dimensions and ROI view together, after decoding.
                this.imgSize = { w: img.naturalWidth, h: img.naturalHeight }
                this.displayView = view
                this.displayRois = this.rois.slice()
                this.displaySrc = url
                this.resetTransform()
            } catch (_) {
                if (request !== this._imageRequest) return
                this.displayView = view
                this.displaySrc = FALLBACK_IMG
            }
        },
        measureStage() {
            const el = this.$refs.stage
            if (!el) return
            const rect = el.getBoundingClientRect()
            const w = Math.max(0, Math.floor(rect.width))
            const h = Math.max(0, Math.floor(rect.height))
            // 弹层/滚动导致瞬时 0 尺寸时保留上次有效值，避免热图被拆掉
            if ((!w || !h) && this.stageSize.w && this.stageSize.h) return
            this.stageSize = { w, h }
        },
        onImageLoad(e) {
            const img = e.target
            const w = img.naturalWidth || 0
            const h = img.naturalHeight || 0
            if (!w || !h) return
            this.imgSize = { w, h }
            this.$nextTick(() => {
                this.measureStage()

            })
        },
        onImageError() {
            console.warn('[infrared] image unavailable')
            // 微信破图常显示「?」；回退占位图，避免偶现问号 icon
            if (this.displaySrc !== FALLBACK_IMG) {
                this.displaySrc = FALLBACK_IMG
                return
            }
            if (!this.imgSize.w || !this.imgSize.h) {
                this.imgSize = { w: 0, h: 0 }
            }
        },
        // N8 supplies source-image pixels; invalid points must not create guessed regions.
        normalizePoints(points) {
            if (!Array.isArray(points)) return [];
            return points.every(p => Number.isFinite(p.x) && Number.isFinite(p.y) && p.x >= 0 && p.y >= 0 && p.x <= this.imgSize.w && p.y <= this.imgSize.h) ? points : [];
        },
        toBoundingRect(points) {
            if (!points || !points.length) return null
            const xs = points.map(p => Number(p.x)).filter(Number.isFinite)
            const ys = points.map(p => Number(p.y)).filter(Number.isFinite)
            if (!xs.length || !ys.length) return null
            const minX = Math.min.apply(null, xs)
            const maxX = Math.max.apply(null, xs)
            const minY = Math.min.apply(null, ys)
            const maxY = Math.max.apply(null, ys)
            const width = maxX - minX
            const height = maxY - minY
            if (width <= 0 || height <= 0) return null
            // 胶囊形：圆角取短边一半，两端成半圆
            const radius = Math.min(width, height) / 2
            return { x: minX, y: minY, width, height, radius }
        },
        resetTransform() {
            this.transform = { scale: 1, x: 0, y: 0 }
        },
        onSelectRoi(key) {
            if (this.dragging.moved) return
            this.$emit('select', key)
        },
        onWheel(e) {
            const next = this.transform.scale + (e.deltaY < 0 ? 0.12 : -0.12)
            this.transform.scale = Math.min(4, Math.max(0.7, Number(next.toFixed(2))))
        },
        onPointerDown(e) {
            this.dragging.active = true
            this.dragging.moved = false
            this.dragging.startX = e.clientX
            this.dragging.startY = e.clientY
            this.dragging.originX = this.transform.x
            this.dragging.originY = this.transform.y
        },
        onPointerMove(e) {
            if (!this.dragging.active) return
            const dx = e.clientX - this.dragging.startX
            const dy = e.clientY - this.dragging.startY
            if (Math.abs(dx) + Math.abs(dy) > 4) this.dragging.moved = true
            this.transform.x = this.dragging.originX + dx
            this.transform.y = this.dragging.originY + dy
        },
        onPointerUp() {
            this.dragging.active = false
        },
        touchDistance(touches) {
            const a = touches[0]
            const b = touches[1]
            const dx = a.clientX - b.clientX
            const dy = a.clientY - b.clientY
            return Math.sqrt(dx * dx + dy * dy)
        },
        onTouchStart(e) {
            if (e.touches.length === 2) {
                this.pinch.active = true
                this.pinch.startDist = this.touchDistance(e.touches)
                this.pinch.startScale = this.transform.scale
                this.dragging.active = false
                return
            }
            if (e.touches.length === 1) {
                const t = e.touches[0]
                this.dragging.active = true
                this.dragging.moved = false
                this.dragging.startX = t.clientX
                this.dragging.startY = t.clientY
                this.dragging.originX = this.transform.x
                this.dragging.originY = this.transform.y
            }
        },
        onTouchMove(e) {
            if (this.pinch.active && e.touches.length === 2) {
                const dist = this.touchDistance(e.touches)
                if (!this.pinch.startDist) return
                const ratio = dist / this.pinch.startDist
                const next = this.pinch.startScale * ratio
                this.transform.scale = Math.min(4, Math.max(0.7, Number(next.toFixed(2))))
                this.dragging.moved = true
                return
            }
            if (!this.dragging.active || e.touches.length !== 1) return
            const t = e.touches[0]
            const dx = t.clientX - this.dragging.startX
            const dy = t.clientY - this.dragging.startY
            if (Math.abs(dx) + Math.abs(dy) > 4) this.dragging.moved = true
            this.transform.x = this.dragging.originX + dx
            this.transform.y = this.dragging.originY + dy
        },
        onTouchEnd(e) {
            if (!e.touches || e.touches.length < 2) this.pinch.active = false
            if (!e.touches || e.touches.length === 0) this.dragging.active = false
        },
    },
}
</script>

<style lang="less" scoped>
.image-unavailable { margin: 0; padding: 60px 8px 12px; color: #8fa2c0; text-align: center; font-size: 12px; overflow-wrap: anywhere; }
.ir-heatmap-viewer {
    position: relative;
    width: 100%;
    height: 100%;
    min-height: 0;
}

.viewer-toolbar {
    position: absolute;
    z-index: 12;
    right: 8px;
    top: 8px;
    display: flex;
    gap: 6px;
}

.tool-btn {
    min-width: 34px;
    height: 28px;
    padding: 0 8px;
    border: 1px solid rgba(2, 215, 228, 0.48);
    border-radius: 8px;
    color: #02d7e4;
    background: rgba(4, 22, 48, 0.74);
    font-size: 12px;
}

.viewer-stage {
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;
    border-radius: 22px;
    touch-action: none;
    cursor: grab;
    background: radial-gradient(ellipse at 50% 45%, rgba(255, 255, 255, 0.08), transparent 58%);

    &:active {
        cursor: grabbing;
    }
}

.viewer-content {
    position: relative;
    width: 100%;
    height: 100%;
    transform-origin: center center;
    will-change: transform;
}

.media-fit {
    position: absolute;
    z-index: 1;
}

.heatmap-img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: fill;
    user-select: none;
    pointer-events: none;

    &.is-probe {
        position: absolute;
        opacity: 0;
        width: 1px;
        height: 1px;
        pointer-events: none;
    }
}

.roi-overlay {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;

    rect {
        pointer-events: auto;
        cursor: pointer;
        fill: rgba(2, 215, 228, 0.06);
        stroke: rgba(2, 215, 228, 0.42);
        stroke-width: 1.5;
        vector-effect: non-scaling-stroke;
        transition: fill 0.15s ease, stroke 0.15s ease, stroke-width 0.15s ease;

        &.is-active {
            fill: rgba(2, 215, 228, 0.28);
            stroke: #02d7e4;
            stroke-width: 2;
        }
    }
}
</style>
